
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Language, ScaleLog } from '../types';
import { t } from '../locales';
import { X, CheckCircle2, Weight, Activity, Droplets, Trash2, Calendar } from 'lucide-react';

interface ScaleModalProps {
  isOpen: boolean;
  onClose: () => void;
  logs: ScaleLog[];
  onSave: (log: ScaleLog) => void;
  onDelete: (id: string) => void;
  language: Language;
  currentWeight?: number;
}

const ScaleModal: React.FC<ScaleModalProps> = ({ 
    isOpen, onClose, logs, onSave, onDelete, language, currentWeight 
}) => {
  const [weight, setWeight] = useState<string>('');
  const [bodyFat, setBodyFat] = useState<string>('');
  const [hydration, setHydration] = useState<string>('');
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
      if (isOpen) {
          setWeight(currentWeight ? currentWeight.toString() : '');
          setBodyFat('');
          setHydration('');
      }
  }, [isOpen, currentWeight]);

  if (!isOpen) return null;

  const handleSave = () => {
      if (!weight) return;
      
      const newLog: ScaleLog = {
          id: crypto.randomUUID(),
          weight: parseFloat(weight),
          bodyFat: bodyFat ? parseFloat(bodyFat) : undefined,
          hydrationPercentage: hydration ? parseFloat(hydration) : undefined,
          timestamp: new Date()
      };

      onSave(newLog);
      
      setShowFeedback(true);
      setTimeout(() => {
          setShowFeedback(false);
          // Optional: Don't close immediately to allow viewing list
      }, 1000);
      
      // Reset optional fields but keep weight for convenience
      setBodyFat('');
      setHydration('');
  };

  // Sort logs descending
  const historyLogs = [...logs].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-2xl shadow-2xl p-6 relative z-10 animate-popIn border border-gray-100 dark:border-gray-700 flex flex-col max-h-[85vh]">
        <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                <Weight size={20} className="text-indigo-500" />
                Scale Measure
            </h3>
            <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700">
                <X size={20} className="text-gray-400" />
            </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 space-y-6 pr-1">
            
            {/* Input Form */}
            <div className="bg-indigo-50/50 dark:bg-gray-700/30 p-4 rounded-xl space-y-4 border border-indigo-100 dark:border-gray-600">
                
                {/* Weight Input */}
                <div>
                    <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                        <Weight size={12} /> Weight (kg) <span className="text-red-400">*</span>
                    </label>
                    <input 
                        type="number" step="0.1"
                        value={weight}
                        onChange={(e) => setWeight(e.target.value)}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none font-bold text-lg"
                        placeholder="0.0"
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    {/* Body Fat Input */}
                    <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                            <Activity size={12} /> Body Fat %
                        </label>
                        <input 
                            type="number" step="0.1"
                            value={bodyFat}
                            onChange={(e) => setBodyFat(e.target.value)}
                            className="w-full p-3 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none font-bold"
                            placeholder="Optional"
                        />
                    </div>

                    {/* Hydration Input */}
                    <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                            <Droplets size={12} /> Hydration %
                        </label>
                        <input 
                            type="number" step="0.1"
                            value={hydration}
                            onChange={(e) => setHydration(e.target.value)}
                            className="w-full p-3 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none font-bold"
                            placeholder="Optional"
                        />
                    </div>
                </div>

                <button 
                    onClick={handleSave}
                    disabled={!weight}
                    className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white font-bold rounded-xl shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2"
                >
                    {showFeedback ? <CheckCircle2 size={20} /> : "Log Measure"}
                </button>
            </div>

            {/* History List */}
            <div>
                <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                    <Calendar size={12} /> Measurement History
                </h4>
                
                {historyLogs.length === 0 ? (
                    <div className="text-center text-gray-400 text-xs italic py-4">No measurements recorded.</div>
                ) : (
                    <div className="space-y-2">
                        {historyLogs.map(log => (
                            <div key={log.id} className="bg-gray-50 dark:bg-gray-900/50 p-3 rounded-xl border border-gray-100 dark:border-gray-700 flex justify-between items-center group">
                                <div>
                                    <div className="text-[10px] text-gray-400 font-medium mb-0.5">
                                        {new Date(log.timestamp).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                    <div className="flex items-center gap-3 text-sm">
                                        <span className="font-bold text-gray-800 dark:text-white">{log.weight} kg</span>
                                        {log.bodyFat && (
                                            <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                                <Activity size={10} className="text-rose-400" /> {log.bodyFat}%
                                            </span>
                                        )}
                                        {log.hydrationPercentage && (
                                            <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                                <Droplets size={10} className="text-cyan-400" /> {log.hydrationPercentage}%
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <button 
                                    onClick={() => onDelete(log.id)}
                                    className="p-2 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

        </div>
      </div>
    </div>,
    document.body
  );
};

export default ScaleModal;